import { BankAccountType } from '@graphql/types';
import { parsePhoneNumber } from '@shared/constants/countries';
import * as Yup from 'yup';

// Local part + domain with at least one dot (e.g. user@example.com).
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const INVALID_EMAIL_MESSAGE = 'Invalid email format';
export const BHUTAN_PHONE_MESSAGE = 'Invalid phone number. Must be 8 digits';
export const INVALID_PHONE_MESSAGE = 'Invalid phone number. Must be 7 to 15 digits';

export const isValidEmail = (value: string): boolean => {
  const trimmed = value.trim();

  return trimmed.length > 0 && EMAIL_REGEX.test(trimmed);
};

export const validatePhoneNumber = (
  value: string | null | undefined,
  options?: { required?: boolean; requiredMessage?: string }
): string | null => {
  const required = options?.required ?? true;
  const requiredMessage = options?.requiredMessage ?? 'Phone number is required';

  if (!value || value.trim() === '') {
    return required ? requiredMessage : null;
  }

  const { country, localNumber } = parsePhoneNumber(value);
  const digitsOnly = localNumber.replace(/\s/g, '');

  if (!digitsOnly) {
    return required ? requiredMessage : null;
  }

  if (country.code === 'BT') {
    if (!/^\d{8}$/.test(digitsOnly)) {
      return BHUTAN_PHONE_MESSAGE;
    }

    return null;
  }

  if (!/^\d{7,15}$/.test(digitsOnly)) {
    return INVALID_PHONE_MESSAGE;
  }

  return null;
};

export const emailFieldSchema = (options?: {
  required?: boolean;
  requiredMessage?: string;
}) => {
  const required = options?.required ?? true;
  const requiredMessage = options?.requiredMessage ?? 'Email is required';

  return Yup.string().test('is-valid-email', function (value) {
    const trimmed = value?.trim() ?? '';

    if (!trimmed) {
      if (required) {
        return this.createError({ message: requiredMessage });
      }

      return true;
    }

    if (!EMAIL_REGEX.test(trimmed)) {
      return this.createError({ message: INVALID_EMAIL_MESSAGE });
    }

    return true;
  });
};

export const phoneNumberFieldSchema = (options?: {
  required?: boolean;
  requiredMessage?: string;
}) => {
  const required = options?.required ?? true;
  const requiredMessage = options?.requiredMessage ?? 'Phone number is required';

  return Yup.string()
    .nullable()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .test('is-valid-phone', function (value) {
      const error = validatePhoneNumber(value, { required, requiredMessage });

      if (error) {
        return this.createError({ message: error });
      }

      return true;
    });
};

export const bankIdFieldSchema = (options?: {
  required?: boolean;
  requiredMessage?: string;
}) => {
  const required = options?.required ?? true;
  const requiredMessage = options?.requiredMessage ?? 'Bank is required';

  let schema = Yup.string().trim();

  if (required) {
    schema = schema.required(requiredMessage);
  }

  return schema;
};

export const bankAccountNumberFieldSchema = (options?: {
  required?: boolean;
  requiredMessage?: string;
  digitsOnlyMessage?: string;
  minDigits?: number;
  maxDigits?: number;
  minMessage?: string;
  maxMessage?: string;
}) => {
  const required = options?.required ?? true;
  const requiredMessage = options?.requiredMessage ?? 'Account number is required';
  const digitsOnlyMessage = options?.digitsOnlyMessage ?? 'Account number must contain only digits';
  const minDigits = options?.minDigits ?? 4;
  const maxDigits = options?.maxDigits ?? 5;
  const minMessage = options?.minMessage ?? `Account number must be at least ${minDigits} digits`;
  const maxMessage = options?.maxMessage ?? `Account number must be at most ${maxDigits} digits`;

  let schema = Yup.string().trim();

  if (required) {
    schema = schema.required(requiredMessage);
  }

  return schema
    .matches(/^\d+$/, digitsOnlyMessage)
    .min(minDigits, minMessage)
    .max(maxDigits, maxMessage);
};

export const accountHolderNameFieldSchema = (options?: {
  required?: boolean;
  requiredMessage?: string;
  min?: number;
  minMessage?: string;
}) => {
  const required = options?.required ?? true;
  const requiredMessage = options?.requiredMessage ?? 'Account name is required';
  const min = options?.min ?? 2;
  const minMessage = options?.minMessage ?? `Account name must be at least ${min} characters`;

  let schema = Yup.string().trim();

  if (required) {
    schema = schema.required(requiredMessage);
  }

  return schema.min(min, minMessage);
};

export const bankAccountTypeFieldSchema = (options?: {
  required?: boolean;
  requiredMessage?: string;
  invalidMessage?: string;
}) => {
  const required = options?.required ?? true;
  const requiredMessage = options?.requiredMessage ?? 'Account type is required';
  const invalidMessage = options?.invalidMessage ?? 'Invalid account type';
  const allowedValues = Object.values(BankAccountType);

  return Yup.string().test('is-valid-bank-account-type', function (value) {
    const trimmed = value?.trim() ?? '';

    if (!trimmed) {
      if (required) {
        return this.createError({ message: requiredMessage });
      }

      return true;
    }

    if (!allowedValues.includes(trimmed as BankAccountType)) {
      return this.createError({ message: invalidMessage });
    }

    return true;
  });
};

export const bankAccountFieldsSchema = {
  bankId: bankIdFieldSchema(),
  accountNumber: bankAccountNumberFieldSchema(),
  accountHolderName: accountHolderNameFieldSchema(),
  type: bankAccountTypeFieldSchema(),
};
